import { isBooleanFalse, isBooleanTrue, isListNotEmpty, isNotNullOrUndefined, isNumberEmpty, isNumberGreaterThanOrEqualToZero, isNumberGreaterThanZero, isNumberNotEmpty, isObjectNotEmpty, isStringEmpty, isStringNotEmpty, isStringNotEmptyOrZeroLength } from '../utils/objectUtils';
import { v4 as uuid } from 'uuid';
import { db, storage } from '../firebase';
import { addObjectToChildArray, removeObjectFromChildArray, subCollectionCreate, subCollectionGetAll, subCollectionGetByParentIdAndSubId, subCollectionGetBySingleQuery, subCollectionRemove, subCollectionUpdate, subCollectionUpdateField, subCollectionUpdateFields, updateObjectFieldInChildArray, updateObjectFieldsInChildArray, updateObjectInChildArray } from '../utils/dbUtils';
import { collectionGroup, endBefore, getCountFromServer, getDocs, limit, limitToLast, orderBy, query, startAfter, where } from 'firebase/firestore';
import { toLower } from 'lodash';
import { convertToSlug } from '../utils/stringFormatter';
import { ref, uploadBytes } from 'firebase/storage';
import Variant from './Variant';
import ProductDimentions from './ProductDimensions';
import ProductVariantSupplierMapping from './ProductVariantSupplierMapping';

class ProductVariant {
	static collectionName = 'products';
	static subCollectionName = 'variants';

	id = uuid();
	productId = null;
	name = null;
	description = null;
	slug = convertToSlug(this.name);
	sku = null;

	enabled = false;

	attributes = []; // array of product attributes

	dimensions = new ProductDimentions().toDto();
	weight = 0.0;

	#image = null;
	imageRefId = null;

	metaData = []; // array of product meta data

	regularPrice = 0.0; // per unit price
	salePrice = null; // optional and will override the regular price
	compareAtPrice = null; // optional and will show a strike through as if the product is on discount
	isSale = false; // is the product on sale, use salePrice instead if true

	manageStock = false;
	lowStockAmount = 0;
	stockStatus = "IN_STOCK"; // Enum StockStatuses
	stockQuantity = 0;

	displayOrder = 0;

	isVirtual = false; // is the product a service with no physical nature

	brand = null;
	gtin = null;
	msrp = null;

	duration = 0; // added 12/5/2023 for services such as telehealth visits in minutes

	count = 1;
	countUnits = null;
	packaging = null;

	// for medications
	instructions = null;
	strength = null;
	form = null; // form of the medication (e.g., tablet, liquid)
	route = null; // route of administration (e.g., oral, intravenous)

	requiresConfirmation = false; // requires patient confirmation - needs to be set to true if there are surveys and they need to be filled out for each order
	requiresClinicianApproval = false; // requires that the doctor prescribes/approves the variant
	requiresClinicianEncounter = false; // requires an in-person or telehealth dr appt to receive this variant if not already receiving this variant
	requiresSurveys = false; // requires patient to fill out forms/surveys

	// require documents to be provided when ordering variant
	requiredDocuments = []; // array of object { documentType, requirement, scope } requirement: enum DocumentRequirements, scope: enum SurveyScopes

	titrationLevel = 1; // the index of the dosage
	titrationPeriods = 1; // number of periods that patient has to stay at this dosage before moving up to a higher dosage
	titrationSchedule = 'NONE'; // type of titration schedule NONE, STANDARD, ALTERNATIVE, SPLIT, RAPID

	supplierMappings = []; // array of ProductVariantSupplierMapping

	upcharge = 0;

	constructor(variant) {
		if (isObjectNotEmpty(variant)) {
			if (isStringNotEmpty(variant.id)) { this.id = variant.id; }
			if (isStringNotEmpty(variant.productId)) { this.productId = variant.productId; }
			if (isStringNotEmpty(variant.name)) { this.name = variant.name; }
			if (isStringNotEmpty(variant.slug)) { this.slug = toLower(variant.slug); }
			else { this.slug = convertToSlug(this.name); }
			if (isStringNotEmpty(variant.description)) { this.description = variant.description; }
			if (isStringNotEmpty(variant.sku)) { this.sku = variant.sku; }

			if (isBooleanTrue(variant.enabled)) { this.enabled = true; }

			if (isListNotEmpty(variant.attributes)) { this.attributes = variant.attributes; }

			if (isNumberNotEmpty(variant.width) && isNumberNotEmpty(variant.height) && isNumberNotEmpty(variant.length)) { this.dimensions = new ProductDimentions(variant.width, variant.height, variant.length).toDto(); }
			if (isNumberGreaterThanOrEqualToZero(variant.weight)) { this.weight = variant.weight; }

			if (isNotNullOrUndefined(variant.image)) { this.#image = variant.image; }
			if (isStringNotEmpty(variant.imageRefId)) { this.imageRefId = variant.imageRefId; }

			if (isListNotEmpty(variant.metaData)) { this.metaData = variant.metaData; }

			if (isNumberGreaterThanOrEqualToZero(variant.regularPrice)) { this.regularPrice = variant.regularPrice; }
			if (isNumberGreaterThanOrEqualToZero(variant.salePrice)) { this.salePrice = variant.salePrice; }
			if (isNumberGreaterThanOrEqualToZero(variant.compareAtPrice)) { this.compareAtPrice = variant.compareAtPrice; }
			if (isBooleanTrue(variant.isSale)) { this.isSale = true; }

			if (isBooleanTrue(variant.manageStock)) { this.manageStock = true; }
			if (isStringNotEmpty(variant.stockStatus)) { this.stockStatus = variant.stockStatus; }
			if (isNumberGreaterThanOrEqualToZero(variant.stockQuantity)) { this.stockQuantity = variant.stockQuantity; }
			if (isNumberGreaterThanOrEqualToZero(variant.lowStockAmount)) { this.lowStockAmount = variant.lowStockAmount; }

			if (isNumberGreaterThanOrEqualToZero(variant.displayOrder)) { this.displayOrder = variant.displayOrder; }

			if (isBooleanTrue(variant.isVirtual)) { this.isVirtual = true; }

			if (isNumberGreaterThanOrEqualToZero(variant.msrp)) { this.msrp = variant.msrp; }
			if (isStringNotEmpty(variant.brand)) { this.brand = variant.brand; }
			if (isStringNotEmpty(variant.gtin)) { this.gtin = variant.gtin; }

			if (isNumberGreaterThanOrEqualToZero(variant.duration)) { this.duration = variant.duration; }

			if (isNumberNotEmpty(variant.count)) { this.count = variant.count; }
			if (isStringNotEmpty(variant.countUnits)) { this.countUnits = variant.countUnits; }
			if (isStringNotEmpty(variant.packaging)) { this.packaging = variant.packaging; }

			if (isStringNotEmpty(variant.instructions)) { this.instructions = variant.instructions; }
			if (isStringNotEmpty(variant.strength)) { this.strength = variant.strength; }
			if (isStringNotEmpty(variant.form)) { this.form = variant.form; }
			if (isStringNotEmpty(variant.route)) { this.route = variant.route; }

			if (isBooleanTrue(variant.requiresConfirmation)) { this.requiresConfirmation = true; }
			if (isBooleanTrue(variant.requiresClinicianApproval)) { this.requiresClinicianApproval = true; }
			if (isBooleanTrue(variant.requiresClinicianEncounter)) { this.requiresClinicianEncounter = true; }
			if (isBooleanTrue(variant.requiresSurveys)) { this.requiresSurveys = true; }

			if (isListNotEmpty(variant.requiredDocuments)) { this.requiredDocuments = variant.requiredDocuments; }

			if (isNumberGreaterThanOrEqualToZero(variant.titrationLevel)) { this.titrationLevel = variant.titrationLevel; }
			if (isNumberGreaterThanOrEqualToZero(variant.titrationPeriods)) { this.titrationPeriods = variant.titrationPeriods; }
			if (isStringNotEmpty(variant.titrationSchedule)) { this.titrationSchedule = variant.titrationSchedule; }

			if (isListNotEmpty(variant.supplierMappings)) { this.supplierMappings = variant.supplierMappings.map((supplierMapping) => new ProductVariantSupplierMapping(supplierMapping).toDto()); }

			if (isNumberGreaterThanOrEqualToZero(variant.upcharge)) { this.upcharge = variant.upcharge; }
		}
	}

	toDto = () => {
		return {
			id: this.id,
			productId: this.productId,
			name: this.name,
			description: this.description,
			slug: this.slug,
			sku: this.sku,

			enabled: this.enabled,

			attributes: this.attributes,

			dimensions: this.dimensions,
			weight: this.weight,

			imageRefId: this.imageRefId,

			metaData: this.metaData,

			regularPrice: this.regularPrice,
			salePrice: this.salePrice,
			compareAtPrice: this.compareAtPrice,
			isSale: this.isSale,

			manageStock: this.manageStock,
			stockStatus: this.stockStatus,
			stockQuantity: this.stockQuantity,
			lowStockAmount: this.lowStockAmount,

			displayOrder: this.displayOrder,

			isVirtual: this.isVirtual,

			brand: this.brand,
			gtin: this.gtin,
			msrp: this.msrp,

			duration: this.duration,

			count: this.count,
			countUnits: this.countUnits,
			packaging: this.packaging,

			instructions: this.instructions,
			strength: this.strength,
			form: this.form,
			route: this.route,

			requiresClinicianEncounter: this.requiresClinicianEncounter,

			requiresConfirmation: this.requiresConfirmation,
			requiresClinicianApproval: this.requiresClinicianApproval,
			requiresClinicianEncounter: this.requiresClinicianEncounter,
			requiresSurveys: this.requiresSurveys,

			requiredDocuments: this.requiredDocuments,

			titrationLevel: this.titrationLevel,
			titrationPeriods: this.titrationPeriods,
			titrationSchedule: this.titrationSchedule,

			supplierMappings: this.supplierMappings,

			upcharge: this.upcharge,
		};
	}

	getPrice = () => {
		if (isBooleanTrue(this.isSale) && isNumberGreaterThanZero(this.salePrice)) {
			return this.salePrice;
		} else {
			return this.regularPrice;
		}
	}

	getComparePrice = () => {
		let price = 0;
		let savings = 0;
		let savingsPercentage = 0;
		if (isBooleanTrue(this.isSale) && isNumberGreaterThanZero(this.salePrice)) {
			price = this.regularPrice;
			savings = this.regularPrice - this.salePrice;
			savingsPercentage = (savings / price) * 100;
		} else if (isNumberGreaterThanZero(this.msrp)) {
			price = this.msrp;
			savings = this.msrp - this.regularPrice;
			savingsPercentage = (savings / price) * 100;
		} else if (isNumberGreaterThanZero(this.compareAtPrice)) {
			price = this.compareAtPrice;
			savings = this.compareAtPrice - this.regularPrice;
			savingsPercentage = (savings / price) * 100;
		} else {
			price = this.regularPrice;
			savings = 0;
			savingsPercentage = 0;
		}

		return { price, savings, savingsPercentage };
	}

	isValid = () => {
		if (isStringEmpty(this.productId)) {
			throw new Error('ProductId is required.');
		}
		if (isStringEmpty(this.name)) {
			throw new Error('Name is required.');
		}
		if (isNumberEmpty(this.regularPrice)) {
			throw new Error('Regular price is required.');
		}
		if (isStringEmpty(this.sku)) {
			throw new Error('SKU is required.');
		}

		return true;
	}

	static addFile = async (file) => {
		if (isNotNullOrUndefined(file)) {
			try {
				const fileName = uuid() + '.' + ProductVariant.getFileExt(file);
				const storageRef = ref(storage, 'products/' + fileName);

				return await uploadBytes(storageRef, file);
			} catch (err) {
				console.log(err);
			}
		}

		return null;
	}

	static getFileExt = (file) => {
		const lastDot = file.name.lastIndexOf('.');
		//const fileName = fullFileName.substring(0, lastDot);
		const ext = file.name.substring(lastDot + 1);

		return toLower(ext);
	}

	static uploadImage = async (file) => {
		let fileRefId = null;
		if (isNotNullOrUndefined(file)) {
			const uploadRes = await ProductVariant.addFile(file);
			// console.log(uploadRes);
			if (isObjectNotEmpty(uploadRes)) {
				fileRefId = uploadRes.ref.fullPath;
			}
		}

		return fileRefId;
	}

	static updateImage = async (productId, variantId, file) => {
		if (isNotNullOrUndefined(file)) {
			const uploadRes = await ProductVariant.addFile(file);
			// console.log(uploadRes);
			if (isObjectNotEmpty(uploadRes)) {
				return await subCollectionUpdateField(ProductVariant.collectionName, productId, ProductVariant.subCollectionName, variantId, 'imageRefId', uploadRes.ref.fullPath);
			}
		}

		return false;
	}

	create = async () => {
		if (this.isValid()) {
			if (isNotNullOrUndefined(this.#image)) {
				this.imageRefId = await ProductVariant.uploadImage(this.#image);
			}

			const newVariant = await subCollectionCreate(ProductVariant.collectionName, this.productId, ProductVariant.subCollectionName, this.id, this.toDto());
			if (isObjectNotEmpty(newVariant)) {
				// add to product variant array
				const success = await addObjectToChildArray(ProductVariant.collectionName, this.productId, 'variants', 'id', new Variant(this.toDto()).toDto());
				if (isBooleanTrue(success)) {
					return newVariant;
				}
			}
		}

		return null;
	};

	static updateField = async (productId, variantId, fieldName, newValue) => {
		if (isStringNotEmpty(productId) && isStringNotEmpty(variantId)) {
			const success = await subCollectionUpdateField(ProductVariant.collectionName, productId, ProductVariant.subCollectionName, variantId, fieldName, newValue);
			if (isBooleanTrue(success)) {
				return await updateObjectFieldInChildArray(ProductVariant.collectionName, productId, 'variants', 'id', variantId, fieldName, newValue);
			}
		} else {
			throw new Error("productId and variantId are required.");
		}
	}

	static updateFields = async (productId, variantId, updatedFields) => {
		if (isStringNotEmpty(productId) && isStringNotEmpty(variantId)) {
			const success = await subCollectionUpdateFields(ProductVariant.collectionName, productId, ProductVariant.subCollectionName, variantId, updatedFields);
			if (isBooleanTrue(success)) {
				return await updateObjectFieldsInChildArray(ProductVariant.collectionName, productId, 'variants', 'id', variantId, updatedFields);
			}
		} else {
			throw new Error("productId and variantId are required.");
		}
	}

	static update = async (productId, variantId, updatedVariant, oldVariant) => {
		let updatedObj = new ProductVariant(updatedVariant);
		if (updatedObj.isValid() && productId === updatedVariant.productId && variantId === updatedVariant.id) {
			if (isNotNullOrUndefined(updatedObj.#image)) {
				updatedObj.imageRefId = await ProductVariant.uploadImage(updatedObj.#image);
			}

			const success = await subCollectionUpdate(ProductVariant.collectionName, productId, ProductVariant.subCollectionName, variantId, updatedObj.toDto());
			const success2 = await updateObjectInChildArray(ProductVariant.collectionName, productId, 'variants', 'id', new Variant({ ...updatedObj.toDto() }).toDto());
			if (isBooleanTrue(success) && isBooleanTrue(success2)) {
				return { ...oldVariant, ...updatedObj.toDto() };
			}
		} else {
			throw new Error('productId and variantId do not match the id and productId fields of the updated variant record.')
		}

		return null;
	}

	static delete = async (productId, variantId) => {
		if (isStringNotEmpty(productId) && isStringNotEmpty(variantId)) {
			const success = await subCollectionRemove(ProductVariant.collectionName, productId, ProductVariant.subCollectionName, variantId);
			if (isBooleanTrue(success)) {
				return await removeObjectFromChildArray(ProductVariant.collectionName, productId, 'variants', 'id', variantId);
			}
		} else {
			throw new Error("productId and variantId are required.");
		}
	}

	static getById = async (productId, variantId) => {
		if (isStringNotEmpty(productId) && isStringNotEmpty(variantId)) {
			return await subCollectionGetByParentIdAndSubId(ProductVariant.collectionName, productId, ProductVariant.subCollectionName, variantId);
		}

		return null;
	}

	static getAll = async () => {
		return await subCollectionGetAll(ProductVariant.subCollectionName);
	}

	static getByField = async (fieldName, fieldValue, comparer = '==') => {
		return await subCollectionGetBySingleQuery(ProductVariant.subCollectionName, fieldName, fieldValue, comparer);
	}

	static getByProductId = async (productId) => {
		return await subCollectionGetBySingleQuery(ProductVariant.subCollectionName, 'productId', productId, '==');
	}

	static getBySku = async (sku) => {
		return await subCollectionGetBySingleQuery(ProductVariant.subCollectionName, 'sku', sku, '==');
	}

	static getBySlug = async (slug) => {
		return await subCollectionGetBySingleQuery(ProductVariant.subCollectionName, 'slug', toLower(slug), '==');
	}

	static getCount = async (queryParameters) => {
		let { productId, enabled, isSale } = queryParameters;

		let queryConstraints = [];

		if (isListNotEmpty(productId)) {
			queryConstraints.push(where('productId', 'in', productId));
		} else if (isStringNotEmptyOrZeroLength(productId)) {
			queryConstraints.push(where('productId', '==', productId));
		}

		if (isBooleanTrue(enabled)) {
			queryConstraints.push(where('enabled', '==', true));
		} else if (isBooleanFalse(enabled)) {
			queryConstraints.push(where('enabled', '==', false));
		}

		if (isBooleanTrue(isSale)) {
			queryConstraints.push(where('isSale', '==', true));
		} else if (isBooleanFalse(isSale)) {
			queryConstraints.push(where('isSale', '==', false));
		}

		let q = query(collectionGroup(db, ProductVariant.subCollectionName), ...queryConstraints);

		const snapShot = await getCountFromServer(q);

		return snapShot.data().count;
	}

	static getPaginated = async (queryParameters, pageSize = 25, firstVisible, lastVisible, orderByField = 'displayOrder') => {
		let { productId, enabled, isSale } = queryParameters;

		let queryConstraints = [];

		if (isListNotEmpty(productId)) {
			queryConstraints.push(where('productId', 'in', productId));
		} else if (isStringNotEmptyOrZeroLength(productId)) {
			queryConstraints.push(where('productId', '==', productId));
		}

		if (isBooleanTrue(enabled)) {
			queryConstraints.push(where('enabled', '==', true));
		} else if (isBooleanFalse(enabled)) {
			queryConstraints.push(where('enabled', '==', false));
		}

		if (isBooleanTrue(isSale)) {
			queryConstraints.push(where('isSale', '==', true));
		} else if (isBooleanFalse(isSale)) {
			queryConstraints.push(where('isSale', '==', false));
		}

		let q = null;
		if (isStringNotEmpty(firstVisible)) {
			q = query(collectionGroup(db, ProductVariant.subCollectionName), ...queryConstraints, orderBy(orderByField, 'asc'), orderBy('__name__', 'asc'), limitToLast(pageSize), endBefore(firstVisible[orderByField], firstVisible['id']));
		} else if (isStringNotEmpty(lastVisible)) {
			q = query(collectionGroup(db, ProductVariant.subCollectionName), ...queryConstraints, orderBy(orderByField, 'asc'), orderBy('__name__', 'asc'), limit(pageSize), startAfter(lastVisible[orderByField], lastVisible['id']));
		} else {
			q = query(collectionGroup(db, ProductVariant.subCollectionName), ...queryConstraints, orderBy(orderByField, 'asc'), orderBy('__name__', 'asc'), limit(pageSize));
		}

		const snapShot = await getDocs(q);
		return snapShot.docs.map((doc) => {
			return {
				id: doc.id, // append document id to each document
				...doc.data()
			};
		});
	}

	static get = async (queryParameters) => {
		let allRecords = [];
		try {
			let next = null;

			let totalCount = await ProductVariant.getCount(queryParameters);
			let totalPages = Math.ceil(totalCount / 10000.0);
			// console.log('totalCount: ' + totalCount);
			// console.log('totalPages: ' + totalPages);
			let pages = [];
			for (let i = 0; i < totalPages; i++) {
				pages.push(i + 1);
			}
			for (const page of pages) {
				// console.log('starting page ' + page);

				let list = await ProductVariant.getPaginated(queryParameters, 10000, null, next, 'displayOrder');

				// console.log(list.length);
				allRecords = [...allRecords, ...list];

				// console.log(allRecords.length);

				// console.log('go to next page');
				next = list[list.length - 1];
			}
		} catch (error) {
			console.log(error);
		}

		return allRecords;
	}
}

export default ProductVariant;